import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Heart, Mountain, Tent, PawPrint, Droplets, Download, Target } from "lucide-react";
import { useUnits, formatDistance } from "../lib/units";
import { getAnonFavs, toggleAnonFav } from "../lib/storage";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { coverImageFor, isFallbackImage } from "../lib/stockImages";
import { MudBadge } from "./MudBadge";
import DifficultyPill from "./DifficultyPill";
import SamplePhotoTag from "./SamplePhotoTag";

/**
 * The Match/Scout pill.
 *
 * These are mutually exclusive: a trail either has enough difficulty data
 * to be scored (renders the Match %) or it doesn't (renders "🎯 Scout
 * Needed" in the earthy-gold accent). We return `null` if neither field is
 * present so cards from older API versions don't render an empty chip.
 */
function MatchOrScoutPill({ loc }) {
  if (loc?.needsScout) {
    return (
      <span
        data-testid={`scout-needed-${loc.location_id}`}
        title="No difficulty data yet — be the first walker to help rate this trail."
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide bg-[#F5B841] text-[#2C3A2E] border border-[#B8862A] shadow-sm shadow-[#B8862A]/30"
      >
        <Target size={13} strokeWidth={2.75} className="text-[#2C3A2E]" /> Scout Needed
      </span>
    );
  }
  if (typeof loc?.match_percentage === "number") {
    // Colour tier tracks conversion signal: sage above 70, terracotta 40-69,
    // muted below. Keeps the pill quick to read on a dense feed page.
    const pct = loc.match_percentage;
    const cls = pct >= 70
      ? "bg-brand-primary/10 text-brand-primary border-brand-primary/30"
      : pct >= 40
        ? "bg-brand-accent/10 text-brand-accent border-brand-accent/30"
        : "bg-brand-sand text-brand-subtle border-brand-border";
    return (
      <span
        data-testid={`match-pill-${loc.location_id}`}
        title={loc.personalized ? "Personalised match for your pack" : "Neutral baseline — sign in and fill your Matchmaker profile for a personal score."}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}
      >
        {pct}% {loc.personalized ? "Match" : "Match"}
      </span>
    );
  }
  return null;
}

export default function TrailCard({ loc, weatherEmoji, isCachedOffline = false }) {
  const { units } = useUnits();
  const { user } = useAuth();
  const [fav, setFav] = React.useState(() => getAnonFavs().includes(loc.location_id));

  React.useEffect(() => {
    const handler = () => setFav(getAnonFavs().includes(loc.location_id));
    window.addEventListener("td-favs-changed", handler);
    return () => window.removeEventListener("td-favs-changed", handler);
  }, [loc.location_id]);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (user) {
      try {
        if (fav) await api.delete(`/favorites/${loc.location_id}`);
        else await api.post(`/favorites/${loc.location_id}`);
      } catch {}
    }
    const next = toggleAnonFav(loc.location_id);
    setFav(next);
  };

  const Icon = loc.type === "camping" ? Tent : Mountain;
  const fallbackCover = isFallbackImage(loc);
  return (
    <Link
      to={`/location/${loc.location_id}`}
      data-testid={`trail-card-${loc.location_id}`}
      className="td-card overflow-hidden block transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="relative aspect-[16/9] bg-brand-border/30">
        <img
          src={coverImageFor(loc)}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {fallbackCover && <SamplePhotoTag testId={`sample-photo-tag-${loc.location_id}`} />}
        <button
          data-testid={`fav-toggle-${loc.location_id}`}
          onClick={toggleFav}
          aria-label="Save to favourites"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-brand-surface/90 backdrop-blur flex items-center justify-center border border-brand-border"
        >
          <Heart size={18} className={fav ? "fill-brand-accent text-brand-accent" : "text-brand-subtle"} />
        </button>
        {loc.ozzy_verified && (
          <div
            data-testid={`ozzy-verified-${loc.location_id}`}
            className="absolute top-3 left-3 td-chip bg-brand-accent text-white border-brand-accent"
            title="Personally walked & verified by Ozzy"
          >
            <PawPrint size={12} className="fill-white" /> Ozzy Verified
          </div>
        )}
        {loc.editor_pick && !loc.ozzy_verified && (
          <div className="absolute top-3 left-3 td-chip bg-brand-accent text-white border-brand-accent">
            Editor's pick
          </div>
        )}
        {loc.dog_friendly_rating === "accessible" && (
          <div className="absolute bottom-3 left-3 td-chip bg-brand-primary text-brand-sand border-brand-primary">
            <PawPrint size={12} /> Dog-friendly
          </div>
        )}
        {loc.dog_friendly_rating === "difficult" && (
          <div className="absolute bottom-3 left-3 td-chip bg-[#D19C4C] text-white border-[#D19C4C]">
            <PawPrint size={12} /> Stiles ahead
          </div>
        )}
        {loc.dog_friendly_rating === "restricted" && (
          <div className="absolute bottom-3 left-3 td-chip bg-brand-error text-white border-brand-error">
            No dogs
          </div>
        )}
        {isCachedOffline && (
          <div
            data-testid={`offline-badge-${loc.location_id}`}
            title="Saved for offline — works without signal"
            className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-surface/95 backdrop-blur text-brand-primary border border-brand-primary/40 shadow-sm"
          >
            <Download size={10} /> Ready offline
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs text-brand-subtle">
          <Icon size={14} /> {loc.type === "camping" ? "Campsite" : "Hiking"}
          <span className="opacity-50">•</span>
          <span className="truncate">{loc.region || "United Kingdom"}</span>
        </div>
        <h3 className="font-heading text-lg sm:text-xl text-brand-text mt-1 leading-tight">{loc.name}</h3>
        <div className="flex items-center justify-between mt-3 text-sm">
          <div className="flex items-center gap-1.5 text-brand-subtle">
            <MapPin size={14} /> {formatDistance(loc.distance_km, { units, compact: true })}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {/* Difficulty + climb — only renders when the backend sent us a
                `difficulty` value, i.e. the trail's GPX had <ele> on every
                point. No elevation = no pill, per spec. */}
            {loc.difficulty && (
              <span
                data-testid={`difficulty-climb-${loc.location_id}`}
                className="inline-flex items-center gap-1 text-[11px] text-brand-subtle"
                title={`${loc.ascent_m ?? 0} m total climb`}
              >
                <DifficultyPill difficulty={loc.difficulty} />
                {typeof loc.ascent_m === "number" && (
                  <span className="font-semibold text-brand-text">· {loc.ascent_m} m climb</span>
                )}
              </span>
            )}
            <MatchOrScoutPill loc={loc} />
            <MudBadge avg={loc.mud_level_avg} count={loc.mud_reports_count} testid={`mud-badge-${loc.location_id}`} />
            {loc.water_source_available && (
              <span
                data-testid={`water-badge-${loc.location_id}`}
                title="Water source available along this trail"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E6F2FB] text-[#1E5B91] border border-[#9FCDEC]"
              >
                <Droplets size={11} className="text-[#2E7AB8]" />
              </span>
            )}
            {loc.verified_walks_count > 0 && (
              <span
                data-testid={`paw-count-${loc.location_id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-primary/10 text-brand-primary border border-brand-primary/30"
                title={`${loc.verified_walks_count} walkers verified this trail`}
              >
                <PawPrint size={11} className="fill-brand-primary" /> {loc.verified_walks_count}
              </span>
            )}
            {weatherEmoji && <div className="td-chip">{weatherEmoji}</div>}
          </div>
        </div>
      </div>
    </Link>
  );
}
